import * as React from 'react';

import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFieldArray, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import * as z from 'zod';

import { useUpdateParking } from 'src/features/parkingManagement/api/updateParking';
import { type Parking, WEEKDAY } from 'src/features/parkingManagement/types/parking';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils/messages';
import {
    FormFieldBlock,
    FormFieldBlockLabel,
    FormFieldBlockTitle,
    FormFieldBlockName,
    FormFieldBlockBody,
    FormFieldBlockSeparator,
} from 'src/registry/components/blocks/FormFieldBlock';
import { InputWithInlineTitle } from 'src/registry/components/inputs/InputWithInlineTitle';
import { useToast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import { Form, FormControl, FormField, FormItem, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { Textarea } from 'src/registry/ui/Textarea';
import { useTitleStore } from 'src/stores/useTitleStore';
import { mapErrors } from 'src/utils/errors';
import { generalErrorMessages, generalLabelMessages, generalMessages, generalMessagesDays } from 'src/utils/messages';

type GeneralInfoParkingFormProps = {
    data: Parking;
    refetch: () => void;
};

const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];
const WEEKDAY_LABELS = [
    generalMessagesDays.monday,
    generalMessagesDays.tuesday,
    generalMessagesDays.wednesday,
    generalMessagesDays.thursday,
    generalMessagesDays.friday,
    generalMessagesDays.saturday,
    generalMessagesDays.sunday,
];

const GeneralInfoParkingForm = ({ data, refetch }: GeneralInfoParkingFormProps) => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParams();
    const { toast } = useToast();
    const { setTitle, removeTitle } = useTitleStore();

    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;

    const workTimeSchema = z.object({
        week_day: z.number().min(0).max(6),
        start_time: z.string().regex(timeRegex, formatMessage(parkingManagementMessages.invalidHourFormat)),
        end_time: z.string().regex(timeRegex, formatMessage(parkingManagementMessages.invalidHourFormat)),
    });

    const formSchema = z.object({
        description: z.string().optional(),
        pricing_description: z.string().optional(),
        number_of_floors: z.coerce.number().optional(),
        number_of_spaces: z.number(),
        is_24_7: z.boolean().optional(),
        working_time: z.array(workTimeSchema),
    });

    const defaultWorkingTime: z.infer<typeof workTimeSchema>[] = WEEKDAYS.map((day) => {
        const found = data.working_time_data.find((w) => w.week_day === (day as WEEKDAY));
        if (found)
            return {
                week_day: found.week_day,
                start_time: found.start_time?.slice(0, 5),
                end_time: found.end_time?.slice(0, 5),
            };
        return null;
    }).filter(Boolean) as z.infer<typeof workTimeSchema>[];

    const defaultValues = {
        description: data.description,
        pricing_description: data.pricing_description,
        number_of_floors: data.number_of_floors,
        number_of_spaces: data.number_of_spaces,
        is_24_7: data.is_24_7,
        working_time: defaultWorkingTime,
    };

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues,
        mode: 'onChange',
    });

    // useFieldArray to manage the working_time array
    const { fields, append, remove } = useFieldArray({
        control: form.control,
        name: 'working_time',
    });

    const YesNoSelectorFields = {
        [formatMessage(generalMessages.yes)]: 'true',
        [formatMessage(generalMessages.no)]: 'false',
    };

    React.useEffect(() => {
        if (!data) return;
        setTitle(data.name);
        form.reset(defaultValues);
        return () => {
            removeTitle();
            form.reset();
        };
    }, [data, formatMessage, data.working_time_data]);

    const isDayEnabled = (day: number) => fields.some((f) => f.week_day === day);

    function toggleDay(day: number, enabled: boolean) {
        if (enabled) {
            if (!isDayEnabled(day)) {
                append({ week_day: day, start_time: '', end_time: '' });
            }
        } else {
            const index = fields.findIndex((f) => f.week_day === day);
            if (index !== -1) {
                remove(index);
            }
        }
    }

    function findFieldIndex(day: number) {
        return fields.findIndex((f) => f.week_day === day);
    }

    const { mutate, isPending } = useUpdateParking({
        onSuccess: () => {
            refetch();
            toast({
                title: formatMessage(parkingManagementMessages.parkingChangeSuccess),
                description: formatMessage(parkingManagementMessages.parkingChangeSuccessDetails, { name: data.name }),
            });
        },
        onError: (e) => {
            if (e?.response?.data?.detail) {
                toast({
                    variant: 'destructive',
                    title: formatMessage(generalErrorMessages.somethingWentWrong),
                    description: `${formatMessage(generalLabelMessages.details)}: ${e?.response?.data?.detail}`,
                });
            } else if (e?.response?.data) {
                mapErrors(e.response.data, form.setError);
            }
        },
    });

    const onSubmit = (data: z.infer<typeof formSchema>) => {
        const payloadWorkingTime = data.working_time.reduce<Record<number, { start_time: string; end_time: string }>>(
            (acc, curr) => {
                acc[curr.week_day] = {
                    start_time: curr.start_time,
                    end_time: curr.end_time,
                };
                return acc;
            },
            {},
        );
        const finalPayload = Object.keys(payloadWorkingTime).length === 0 ? null : payloadWorkingTime;

        mutate({
            parkingId,
            description: data.description,
            pricing_description: data.pricing_description,
            number_of_floors: data.number_of_floors,
            number_of_spaces: data.number_of_spaces,
            is_24_7: data.is_24_7 ?? false,
            working_time: finalPayload,
        });
    };
    const {
        formState: { isDirty, isValid },
    } = form;

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <FormFieldBlockSeparator />
            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(parkingManagementMessages.description)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody>
                    <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                            <FormItem className="w-full">
                                <FormControl>
                                    <Textarea {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>
            <FormFieldBlockSeparator />

            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>
                            {formatMessage(parkingManagementMessages.pricesDescription)}
                        </FormFieldBlockName>
                    </FormFieldBlockTitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody>
                    <FormField
                        control={form.control}
                        name="pricing_description"
                        render={({ field }) => (
                            <FormItem className="w-full">
                                <FormControl>
                                    <Textarea {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>
            <FormFieldBlockSeparator />

            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(parkingManagementMessages.floors)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody>
                    <FormField
                        control={form.control}
                        name="number_of_floors"
                        render={({ field }) => (
                            <FormItem className="w-full">
                                <FormControl>
                                    <Input type="number" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>

            <FormFieldBlockSeparator />

            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(parkingManagementMessages.parkingSpots)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody>
                    <FormField
                        control={form.control}
                        name="number_of_spaces"
                        render={({ field }) => (
                            <FormItem className="w-full">
                                <FormControl>
                                    <Input
                                        type="number"
                                        {...field}
                                        onChange={(e) => field.onChange(Number(e.target.value))}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>

            <FormFieldBlockSeparator />

            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(parkingManagementMessages.allDayAccess)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody className="w-full">
                    <FormField
                        control={form.control}
                        name="is_24_7"
                        render={({ field }) => {
                            return (
                                <FormItem>
                                    <FormControl>
                                        <Select
                                            onValueChange={(val) => field.onChange(val === 'true')}
                                            value={String(field.value)}
                                        >
                                            <SelectTrigger>
                                                <SelectValue
                                                    placeholder={formatMessage(parkingManagementMessages.allDayAccess)}
                                                />
                                            </SelectTrigger>

                                            <SelectContent>
                                                {Object.entries(YesNoSelectorFields).map(([key, value]) => {
                                                    return (
                                                        <SelectItem key={key} value={value}>
                                                            {key}
                                                        </SelectItem>
                                                    );
                                                })}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            );
                        }}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>

            <div className="flex flex-col gap-2">
                <h2 className="font-medium">{formatMessage(parkingManagementMessages.workingTime)}</h2>
                <p className="text-gray-600">{formatMessage(parkingManagementMessages.workingTimeDescription)}</p>
            </div>

            {WEEKDAYS.map((day) => {
                const enabled = isDayEnabled(day);
                const index = findFieldIndex(day);

                return (
                    <React.Fragment key={day}>
                        <FormFieldBlock className="flex flex-col gap-2 xl:flex-row xl:gap-4" key={day}>
                            <FormFieldBlockLabel>
                                <FormFieldBlockTitle>
                                    <FormFieldBlockName>{formatMessage(WEEKDAY_LABELS[day])}</FormFieldBlockName>
                                </FormFieldBlockTitle>
                            </FormFieldBlockLabel>

                            <FormFieldBlockBody className="flex flex-col gap-2 xl:flex-row xl:gap-4">
                                <div className="flex-1">
                                    <Select
                                        value={String(enabled)}
                                        onValueChange={(val) => toggleDay(day, val === 'true')}
                                    >
                                        <SelectTrigger
                                            className="min-w-40"
                                            iconAlignRight
                                            inlineTitle={formatMessage(parkingManagementMessages.working)}
                                        >
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="true">{formatMessage(generalMessages.yes)}</SelectItem>
                                            <SelectItem value="false">{formatMessage(generalMessages.no)}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="flex-1">
                                    <InputWithInlineTitle
                                        inlineTitle={formatMessage(parkingManagementMessages.start)}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={5}
                                        placeholder="--:--"
                                        className="pl-16"
                                        disabled={!enabled}
                                        value={!enabled ? '--:--' : undefined}
                                        {...(index !== -1 ? form.register(`working_time.${index}.start_time`) : {})}
                                    />
                                    {form.formState.errors.working_time?.[index]?.start_time && (
                                        <p className="text-xs text-red-500">
                                            {form.formState.errors.working_time[index].start_time.message}
                                        </p>
                                    )}
                                </div>
                                <div className="flex-1">
                                    <InputWithInlineTitle
                                        inlineTitle={formatMessage(parkingManagementMessages.end)}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={5}
                                        placeholder="--:--"
                                        className="pl-16"
                                        disabled={!enabled}
                                        value={!enabled ? '--:--' : undefined}
                                        {...(index !== -1 ? form.register(`working_time.${index}.end_time`) : {})}
                                    />
                                    {form.formState.errors.working_time?.[index]?.end_time && (
                                        <p className="text-xs text-red-500">
                                            {form.formState.errors.working_time[index].end_time.message}
                                        </p>
                                    )}
                                </div>
                            </FormFieldBlockBody>
                        </FormFieldBlock>
                        <FormFieldBlockSeparator />
                    </React.Fragment>
                );
            })}

            <div className="flex justify-end">
                <Button className="w-full sm:w-auto" type="submit" disabled={!isDirty || !isValid}>
                    {isPending && <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin text-lg" />}
                    {formatMessage(generalMessages.saveChanges)}
                </Button>
            </div>
        </Form>
    );
};

export default GeneralInfoParkingForm;
